/**
 * Cycle-aware session recommendation. Maps a cycle phase to one of
 * the existing Be Bold session-type tones (rest / release / power /
 * gentle / build), then returns a structured recommendation the UI
 * can render as a chip + tooltip on WeekHome.
 *
 * Phase to tone mapping is grounded in standard cycle physiology:
 *   - menstrual: deep rest, breathwork, GENTLE if anything
 *   - follicular: rising estrogen, BUILD energy with intention
 *   - ovulatory: peak hormonal expression, POWER lands beautifully
 *   - luteal: descending energy, RELEASE meets the body where it is
 *
 * For users in peri / meno / post the phase signal is unreliable or
 * absent (cycles vary 7+ days in late peri, postmenopause has no
 * cycles). In those stages the recommendation becomes stage-focused
 * rather than phase-focused: lean RELEASE for GSM (vaginal dryness,
 * urinary urgency, painful sex), informed by the AUA/SUFU/AUGS 2025
 * GSM guideline + Mercier et al. 2019 PFMT feasibility study.
 *
 * The chip is informational only. We never block or override the
 * planned session, since the program has its own progression. The
 * recommendation is a "this is why your body might feel X today"
 * note, not a session swap.
 */

import { isMenopauseModeActive } from './menopause';

export const PHASE_TONES = {
  menstrual:  'rest',
  follicular: 'build',
  ovulatory:  'power',
  luteal:     'release',
};

/**
 * Stage-driven tones that override phase-based tones when the user is
 * in menopause mode. Peri leans release because GSM tissue changes +
 * sleep/mood disruption respond best to parasympathetic, low-impact
 * work; meno + post lean release for the same reason plus reduced
 * resilience to high-load contractions.
 */
export const STAGE_TONES = {
  peri: 'release',
  meno: 'release',
  post: 'release',
};

/**
 * Returns a recommendation object for the given phase + stage, or
 * null when neither yields a recommendation. Strings live in
 * src/locales/strings.js under cycle_rec_<phase>_* and
 * menopause_rec_<stage>_* keys; this helper returns the keys + the
 * tone so the consumer can theme accordingly.
 *
 * Stage takes precedence over phase: in peri / meno / post the phase
 * signal is unreliable, and the menopause-specific copy is more
 * relevant to what the user is actually experiencing today.
 *
 * @param {('menstrual'|'follicular'|'ovulatory'|'luteal'|null|undefined)} phase
 * @param {('pre'|'peri'|'meno'|'post'|'unsure'|null|undefined)} [stage]
 * @returns {{ tone: string, headlineKey: string, bodyKey: string }|null}
 */
export function sessionRecommendationForPhase(phase, stage) {
  if (isMenopauseModeActive(stage)) {
    return {
      tone: STAGE_TONES[stage],
      headlineKey: `menopause_rec_${stage}_headline`,
      bodyKey: `menopause_rec_${stage}_body`,
    };
  }
  if (!phase || !PHASE_TONES[phase]) return null;
  return {
    tone: PHASE_TONES[phase],
    headlineKey: `cycle_rec_${phase}_headline`,
    bodyKey: `cycle_rec_${phase}_body`,
  };
}

/**
 * Color token per recommendation tone. Maps to the brand movement
 * palette so the chip visually links to the cycle phase color used
 * elsewhere (rings, arc, calendar tints).
 */
export function toneColor(tone) {
  switch (tone) {
    case 'rest':    return 'var(--push, #E5736B)';      // coral, like menstrual
    case 'build':   return 'var(--relax, #5FAA9F)';     // teal, like follicular
    case 'power':   return 'var(--primary, #A8003F)';   // magenta, like ovulatory
    case 'release': return 'var(--warm-brown, #8B7355)';// brown, like luteal
    default:        return 'var(--warm-brown, #8B7355)';
  }
}
